import { Navigate } from 'react-router-dom';
import { useMyExternalProjects } from '../../hooks/useMyExternalProjects';
import { LoadingPage } from '../../components/common/Spinner';
import type { ExternalProjectNode } from '../../services/api/externalProjects';

/**
 * external-board-redirect-login follow-up (2026-07-02) — landing behavior.
 *
 * The `/external/projects` index used to render a static "Pick a project from
 * the sidebar to begin." placeholder. Per user request the external user should
 * land directly on the board of their FIRST project after login.
 *
 * "First" = the first navigable (isMember) node in the SAME order the sidebar
 * renders (SidebarProjectTree.tsx): roots sorted by name, depth-first preorder,
 * children sorted by name. Ghost ancestors (isMember=false) are skipped because
 * they are not clickable in the sidebar either.
 *
 * Fallbacks:
 *   - query loading            → LoadingPage (avoids a placeholder flash + a
 *                                second redirect once data arrives)
 *   - no projects / no member  → the original placeholder copy
 */
function firstMemberProjectId(nodes: ExternalProjectNode[]): number | null {
  const ids = new Set(nodes.map((n) => n.id));
  const childMap = new Map<number, ExternalProjectNode[]>();
  const roots: ExternalProjectNode[] = [];
  for (const n of nodes) {
    if (n.parentId === null || !ids.has(n.parentId)) {
      roots.push(n);
    } else {
      const arr = childMap.get(n.parentId) ?? [];
      arr.push(n);
      childMap.set(n.parentId, arr);
    }
  }
  const byName = (a: ExternalProjectNode, b: ExternalProjectNode) =>
    a.name.localeCompare(b.name);
  roots.sort(byName);
  for (const arr of childMap.values()) arr.sort(byName);

  // Depth-first preorder — returns the first isMember node encountered.
  const stack = [...roots].reverse();
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.isMember) return node.id;
    const children = childMap.get(node.id);
    if (children) {
      stack.push(...[...children].reverse());
    }
  }
  return null;
}

export function ProjectsIndexRedirect() {
  const { data, isLoading } = useMyExternalProjects();

  if (isLoading) {
    return <LoadingPage />;
  }

  const targetId = data ? firstMemberProjectId(data) : null;
  if (targetId !== null) {
    return <Navigate to={`/external/projects/${targetId}/board`} replace />;
  }

  return (
    <div className="text-sm font-medium text-gray-500">
      Pick a project from the sidebar to begin.
    </div>
  );
}
